import { Box } from "@mui/material";
import { useMemo } from "react";
import { createPortal } from "react-dom";

import { useAllOpenWindows, useDispatchWindows, useDockedWindows, windowsActions } from "contexts/windows";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";

import { DOCK_MIN_HEIGHT, DOCK_MINIMIZED_HEIGHT, DOCK_PANEL_GAP } from "./dock/constants";
import { useDockLayoutContext } from "./dock/dockLayoutContext";
import { WindowSlot } from "./WindowSlot";

/**
 * Renders all open windows from Redux state.
 *
 * Core windows are registered by CoreWindowsProvider.
 * Dynamic windows can be added via windowsActions.addWindow.
 */
export function WindowManager() {
    const allOpenWindows = useAllOpenWindows();
    const dockedWindows = useDockedWindows();
    const { slots, zoneRect } = useDockLayoutContext();
    const dispatchWindows = useDispatchWindows();
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();
    const floatingWindows = allOpenWindows.filter((windowEntry) => !windowEntry.state.docked);

    const slotMap = useMemo(() => {
        const map = new Map<string, { top: number; height: number; slotIndex: number }>();
        for (let index = 0; index < slots.length; index += 1) {
            const slot = slots[index];
            if (!slot.isPlaceholder) {
                map.set(slot.id, { top: slot.top, height: slot.height, slotIndex: index });
            }
        }
        return map;
    }, [slots]);

    const slotCallbacks = useMemo(() => {
        const map = new Map<
            string,
            {
                onClose: () => void;
                onToggleMinimize: () => void;
                onSetHeight: (height: number) => void;
                onSetMinimizedHeight: (height: number) => void;
                onSetContentHeight: (height: number | undefined) => void;
            }
        >();
        for (const windowEntry of allOpenWindows) {
            map.set(windowEntry.id, {
                onClose: () => dispatchWindows(windowsActions.closeWindow(windowEntry.id)),
                onToggleMinimize: () => dispatchWindows(windowsActions.toggleMinimizedWindow(windowEntry.id)),
                onSetHeight: (height) => dispatchWindows(windowsActions.setDockedHeight(windowEntry.id, height)),
                onSetMinimizedHeight: (height) =>
                    dispatchWindows(windowsActions.setDockedMinimizedHeight(windowEntry.id, height)),
                onSetContentHeight: (height) =>
                    dispatchWindows(windowsActions.setDockedContentHeight(windowEntry.id, height)),
            });
        }
        return map;
    }, [allOpenWindows, dispatchWindows]);

    const maxResizeHeights = useMemo<Map<string, number>>(() => {
        const result = new Map<string, number>();
        const zoneH = zoneRect?.height ?? 0;
        if (!zoneH || dockedWindows.length === 0) {
            return result;
        }
        const reserved = (windowEntry: (typeof dockedWindows)[number]): number => {
            if (windowEntry.state.minimized) {
                return windowEntry.state.dockedMinimizedHeight ?? DOCK_MINIMIZED_HEIGHT;
            }
            return windowEntry.state.dockedHeight ?? DOCK_MIN_HEIGHT;
        };
        const totalReserved = dockedWindows.reduce((sum, windowEntry) => sum + reserved(windowEntry), 0);
        const gapTotal = Math.max(0, dockedWindows.length - 1) * DOCK_PANEL_GAP;
        for (const windowEntry of dockedWindows) {
            const reservedOthers = totalReserved - reserved(windowEntry);
            result.set(windowEntry.id, Math.max(DOCK_MIN_HEIGHT, zoneH - reservedOthers - gapTotal));
        }
        return result;
    }, [dockedWindows, zoneRect]);

    const handleFocusWindow = (windowId: string) => {
        const currentWindow = floatingWindows.find((w) => w.id === windowId);
        if (!currentWindow) {
            return;
        }

        const maxZ = Math.max(0, ...floatingWindows.map((w) => w.state.zIndex || 0));

        if ((currentWindow.state.zIndex || 0) >= maxZ) {
            return;
        }

        dispatchWindows(windowsActions.focusWindow(windowId));
    };

    const container = document.getElementById(ELEMENT_IDS.renderContainer);
    if (!container) {
        return null;
    }

    return createPortal(
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                pointerEvents: "none",
            }}
        >
            {allOpenWindows.map((entry) => {
                const callbacks = slotCallbacks.get(entry.id);
                const slot = slotMap.get(entry.id);
                return (
                    <WindowSlot
                        key={entry.id}
                        entry={entry}
                        onFocus={handleFocusWindow}
                        onClose={() => callbacks?.onClose()}
                        onToggleMinimize={() => callbacks?.onToggleMinimize()}
                        onSetHeight={(height) => callbacks?.onSetHeight(height)}
                        onSetMinimizedHeight={(height) => callbacks?.onSetMinimizedHeight(height)}
                        onSetContentHeight={(height) => callbacks?.onSetContentHeight(height)}
                        maxHeight={maxResizeHeights.get(entry.id) ?? DOCK_MIN_HEIGHT}
                        isLast={slot != null && slot.slotIndex === slots.length - 1}
                        dockTop={slot?.top ?? 0}
                        dockHeight={slot != null && slot.height > 0 ? slot.height : DOCK_MIN_HEIGHT}
                    />
                );
            })}
        </Box>,
        container,
    );
}
